import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Star, Trash2, ArrowUpRight, Loader2, Users, Tag, ChevronDown, ChevronRight } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';

/**
 * "My Views" + "Team Views" — personal bookmark library on the
 * dashboard, with tag-folder grouping and a separate Team Views
 * section for tenant-shared bookmarks (iter377).
 *
 * - Auto-refreshes via the `bookmarks:changed` window event.
 * - Hides itself entirely when the user has zero saved views.
 * - Renders folders by tag — bookmarks without tags fall into "All".
 * - Folder collapse state persisted in localStorage.
 */
export default function DashboardBookmarks() {
  const [rows, setRows] = useState(null);
  const [removingId, setRemovingId] = useState(null);
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem('bookmark-folders-collapsed') || '{}'); }
    catch { return {}; }
  });

  const persistCollapsed = (next) => {
    setCollapsed(next);
    try { localStorage.setItem('bookmark-folders-collapsed', JSON.stringify(next)); }
    catch { /* quota — non-fatal */ }
  };

  const load = useCallback(async () => {
    try {
      const { data } = await api.get('/me/bookmarks');
      setRows(data?.rows || []);
    } catch {
      setRows([]); // fail silent on the dashboard
    }
  }, []);

  useEffect(() => {
    load();
    const handler = () => load();
    window.addEventListener('bookmarks:changed', handler);
    return () => window.removeEventListener('bookmarks:changed', handler);
  }, [load]);

  const remove = async (id, name) => {
    setRemovingId(id);
    try {
      await api.delete(`/me/bookmarks/${id}`);
      toast.success(`Removed "${name}"`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not remove bookmark');
    } finally {
      setRemovingId(null);
    }
  };

  // Split into private vs team, and within private group by tag.
  const groups = useMemo(() => {
    if (!rows) return null;
    const mine = rows.filter(r => r.is_mine);
    const team = rows.filter(r => !r.is_mine);
    // For "mine", build folders by tag. Untagged → "All views".
    const folders = new Map();
    folders.set('all', { tag: 'all', label: 'All views', items: [] });
    for (const b of mine) {
      const tags = (b.tags || []).filter(Boolean);
      if (tags.length === 0) {
        folders.get('all').items.push(b);
      } else {
        for (const t of tags) {
          if (!folders.has(t)) folders.set(t, { tag: t, label: t, items: [] });
          folders.get(t).items.push(b);
        }
      }
    }
    // Drop the "all" bucket if empty + we have other folders.
    if (folders.get('all').items.length === 0 && folders.size > 1) folders.delete('all');
    return { mine, team, folders: Array.from(folders.values()) };
  }, [rows]);

  if (!groups || (groups.mine.length === 0 && groups.team.length === 0)) return null;

  const renderItem = (b, isMine) => (
    <div
      key={`${b.id}-${isMine ? 'mine' : 'team'}`}
      className="group flex items-center gap-2 bg-white rounded-lg border border-border px-3 py-2 hover:border-amber-300 hover:shadow-sm transition-all"
      data-testid={`bookmark-${b.id}`}
    >
      <Link
        to={b.url}
        className="flex-1 min-w-0 flex items-center gap-2 text-sm text-foreground hover:text-amber-700"
        data-testid={`bookmark-go-${b.id}`}
      >
        <span className="truncate" title={b.name}>{b.name}</span>
        <ArrowUpRight size={11} className="opacity-50 shrink-0" />
        {!isMine && b.user_name && (
          <span className="text-[10px] text-muted-foreground truncate" title={`Shared by ${b.user_name}`}>
            · {b.user_name}
          </span>
        )}
      </Link>
      {isMine && (
        <Button
          size="sm"
          variant="ghost"
          className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity text-rose-500 hover:text-rose-700 hover:bg-rose-50"
          disabled={removingId === b.id}
          onClick={(e) => { e.preventDefault(); remove(b.id, b.name); }}
          data-testid={`bookmark-remove-${b.id}`}
          aria-label={`Remove ${b.name}`}
        >
          {removingId === b.id ? <Loader2 size={11} className="animate-spin" /> : <Trash2 size={11} />}
        </Button>
      )}
    </div>
  );

  return (
    <Card className="p-4 bg-gradient-to-br from-amber-50 via-white to-orange-50 border-amber-200/70" data-testid="dashboard-bookmarks">
      {/* My Views */}
      {groups.mine.length > 0 && (
        <>
          <div className="flex items-center gap-2 mb-3">
            <Star size={16} className="text-amber-500" />
            <h3 className="font-outfit font-semibold text-base">My Views</h3>
            <span className="text-xs text-muted-foreground">({groups.mine.length})</span>
          </div>
          <div className="space-y-3">
            {groups.folders.map(f => {
              const isCollapsed = !!collapsed[f.tag];
              return (
                <div key={f.tag} data-testid={`bookmark-folder-${f.tag}`}>
                  <button
                    type="button"
                    className="flex items-center gap-1.5 text-xs font-semibold text-muted-foreground hover:text-foreground mb-1.5"
                    onClick={() => persistCollapsed({ ...collapsed, [f.tag]: !isCollapsed })}
                    data-testid={`bookmark-folder-toggle-${f.tag}`}
                  >
                    {isCollapsed ? <ChevronRight size={12} /> : <ChevronDown size={12} />}
                    {f.tag !== 'all' ? <Tag size={11} /> : <Star size={11} />}
                    <span className="uppercase tracking-wide">{f.label}</span>
                    <Badge variant="outline" className="text-[9px] ml-1 px-1.5 py-0">{f.items.length}</Badge>
                  </button>
                  {!isCollapsed && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                      {f.items.map(b => renderItem(b, true))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Team Views */}
      {groups.team.length > 0 && (
        <>
          <div className={`flex items-center gap-2 mb-3 ${groups.mine.length > 0 ? 'mt-5 pt-4 border-t border-amber-200/60' : ''}`}>
            <Users size={16} className="text-violet-500" />
            <h3 className="font-outfit font-semibold text-base">Team Views</h3>
            <span className="text-xs text-muted-foreground">({groups.team.length})</span>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {groups.team.map(b => renderItem(b, false))}
          </div>
        </>
      )}
    </Card>
  );
}
